import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import { api, apiError } from "../../lib/api";
import { Card, Btn, Empty, Modal, Badge } from "../../components/UI";
import { FLabel, FInput, FSelect, FTextarea } from "./FormControls";
import { DateField } from "./DateField";
import { Plus, Search, Pencil, Trash2, CheckCircle2, BellRing, ArrowUp, ArrowDown, ChevronsUpDown, ListChecks, AlertTriangle, CalendarClock, CheckCheck, ShieldCheck, User } from "lucide-react";
import { toast } from "sonner";
import { TemplatePanel } from "./TemplatePanel";

const PRIORITIES = [["urgent", "Urgent"], ["high", "High"], ["medium", "Medium"], ["low", "Low"]];
const QUICK_DUE = [["3d", "3 days", 3], ["1w", "1 week", 7], ["2w", "2 weeks", 14], ["1m", "1 month", 30], ["3m", "3 months", 90]];
const EMPTY = { title: "", due_date: "", priority: "medium", assignee_id: "", assignee_name: "", reminder_date: "", notes: "" };
const TODAY = () => dayjs().format("YYYY-MM-DD");
const TOMORROW = () => dayjs().add(1, "day").format("YYYY-MM-DD");

function DueCell({ task }) {
  if (!task.due_date) return <span className="text-[#84675B]">-</span>;
  const diff = dayjs(task.due_date).startOf("day").diff(dayjs().startOf("day"), "day");
  const label = dayjs(task.due_date).format("MMM D, YYYY");
  let chip = null;
  if (task.status === "open") {
    if (diff < 0) chip = <span className="rounded-full bg-[#FFEBEE] text-[#C62828] px-2 py-0.5 text-xs font-semibold">Overdue {-diff}d</span>;
    else if (diff === 0) chip = <span className="rounded-full bg-[#FFF3E0] text-[#B84A00] px-2 py-0.5 text-xs font-semibold">Due today</span>;
    else if (diff <= 7) chip = <span className="rounded-full bg-[#FFF8E1] text-[#876600] px-2 py-0.5 text-xs font-semibold">In {diff}d</span>;
  }
  return <div className="flex items-center gap-2"><span>{label}</span>{chip}</div>;
}

function StatChip({ icon: Icon, label, value, tone, active, onClick, testId }) {
  return (
    <button type="button" data-testid={testId} onClick={onClick}
      className={`flex items-center gap-3 rounded-2xl border px-4 py-3 text-left transition-colors ${active ? "border-[#4E342E] bg-[#F5F2EB]" : "border-[#EFEBE0] bg-white hover:bg-[#FDFCF8]"}`}>
      <div className={`h-9 w-9 rounded-xl flex items-center justify-center ${tone}`}><Icon size={17} /></div>
      <div>
        <div className="font-heading text-lg font-bold text-[#3E2723] leading-tight">{value}</div>
        <div className="text-xs text-[#7B6157]">{label}</div>
      </div>
    </button>
  );
}

export default function IntakeTasks() {
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 1, stats: { open: 0, overdue: 0, due_week: 0, completed: 0 } });
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [priority, setPriority] = useState("");
  const [status, setStatus] = useState("open");
  const [dueStart, setDueStart] = useState(TODAY());
  const [dueEnd, setDueEnd] = useState(TODAY());
  const [sort, setSort] = useState("due_date");
  const [order, setOrder] = useState("asc");
  const [page, setPage] = useState(1);
  const [modal, setModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [staff, setStaff] = useState([]);
  const [completingIds, setCompletingIds] = useState([]);
  const [tplOpen, setTplOpen] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const load = () => {
    const params = { page, limit: 10, sort, order, status };
    if (search) params.search = search;
    if (priority) params.priority = priority;
    if (dueStart) params.due_start = dueStart;
    if (dueEnd) params.due_end = dueEnd;
    api.get("/intake-tasks", { params }).then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  };
  useEffect(() => { load(); }, [page, sort, order, status, priority, search, dueStart, dueEnd]);
  useEffect(() => { api.get("/users").then((r) => setStaff(r.data.filter((u) => u.status === "active"))); }, []);

  const toggleSort = (field) => {
    if (sort === field) setOrder(order === "asc" ? "desc" : "asc");
    else { setSort(field); setOrder("asc"); }
  };

  const SortHead = ({ field, children }) => (
    <th className="bg-[#EDE7DC] text-[#4E342E] font-semibold py-3 px-4">
      <button type="button" data-testid={`sort-${field}`} onClick={() => toggleSort(field)} className="flex items-center gap-1.5 hover:text-[#3E2723]">
        {children}
        {sort === field ? (order === "asc" ? <ArrowUp size={13} /> : <ArrowDown size={13} />) : <ChevronsUpDown size={13} className="opacity-40" />}
      </button>
    </th>
  );

  const openAdd = () => { setForm(EMPTY); setEditId(null); setModal(true); };
  const openEdit = (t) => {
    setForm({ title: t.title, due_date: t.due_date || "", priority: t.priority, assignee_id: t.assignee_id || "", assignee_name: t.assignee_name || "", reminder_date: t.reminder_date || "", notes: t.notes || "" });
    setEditId(t.id); setModal(true);
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form };
    const u = staff.find((s) => s.id === form.assignee_id);
    payload.assignee_name = u ? `${u.first_name} ${u.last_name}` : "";
    try {
      if (editId) await api.put(`/intake-tasks/${editId}`, payload);
      else await api.post("/intake-tasks", payload);
      toast.success(editId ? "Task updated" : "Task added");
      setModal(false); load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const complete = async (t) => {
    setCompletingIds((ids) => [...ids, t.id]);
    try {
      await api.put(`/intake-tasks/${t.id}/complete`);
      toast.success("Task completed");
      setTimeout(() => {
        load();
        setCompletingIds((ids) => ids.filter((x) => x !== t.id));
      }, 650);
    } catch (err) {
      setCompletingIds((ids) => ids.filter((x) => x !== t.id));
      toast.error(apiError(err));
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/intake-tasks/${confirmDelete.id}`);
      toast.success("Task deleted");
      setConfirmDelete(null); load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const s = data.stats;
  const today = TODAY();
  const tomorrow = TOMORROW();
  const yesterday = dayjs().subtract(1, "day").format("YYYY-MM-DD");
  const weekEnd = dayjs().add(7, "day").format("YYYY-MM-DD");
  const clearDates = () => { setDueStart(""); setDueEnd(""); setPage(1); };
  const scope = dueStart === today && dueEnd === today ? "today"
    : dueStart === tomorrow && dueEnd === tomorrow ? "tomorrow"
    : !dueStart && !dueEnd ? "all" : "custom";
  const setScope = (sc) => {
    if (sc === "today") { setDueStart(today); setDueEnd(today); }
    else if (sc === "tomorrow") { setDueStart(tomorrow); setDueEnd(tomorrow); }
    else clearDates();
    setPage(1);
  };
  const emptyText = scope === "today" ? "Nothing due today - you're all caught up!"
    : scope === "tomorrow" ? "Nothing due tomorrow yet."
    : "No intake tasks match your filters";

  return (
    <div>
      <TemplatePanel open={tplOpen} onClose={() => setTplOpen(false)} staff={staff} onApplied={load} />
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 mb-5">
        <StatChip icon={ListChecks} label="Open tasks" value={s.open} tone="bg-[#F5F2EB] text-[#6D4C41]" active={status === "open" && !priority && !dueStart && !dueEnd} onClick={() => { setStatus("open"); setPriority(""); clearDates(); }} testId="stat-open" />
        <StatChip icon={AlertTriangle} label="Overdue" value={s.overdue} tone="bg-[#FFEBEE] text-[#C62828]" active={status === "open" && dueEnd === yesterday && !dueStart} onClick={() => { setStatus("open"); setDueStart(""); setDueEnd(yesterday); setSort("due_date"); setOrder("asc"); setPage(1); }} testId="stat-overdue" />
        <StatChip icon={CalendarClock} label="Due this week" value={s.due_week} tone="bg-[#FFF8E1] text-[#876600]" active={status === "open" && dueStart === today && dueEnd === weekEnd} onClick={() => { setStatus("open"); setDueStart(today); setDueEnd(weekEnd); setSort("due_date"); setOrder("asc"); setPage(1); }} testId="stat-due-week" />
        <StatChip icon={CheckCheck} label="Completed" value={s.completed} tone="bg-[#E8F5E9] text-[#2E7D32]" active={status === "completed"} onClick={() => { setStatus("completed"); clearDates(); }} testId="stat-completed" />
      </div>

      <Card>
        <div className="flex flex-wrap items-center gap-3 p-5 pb-2">
          <h1 className="font-heading text-lg font-bold text-[#3E2723] mr-2">All Intake Tasks</h1>
          <div className="relative flex-1 min-w-[220px] max-w-sm">
            <Search size={16} className="absolute left-3 top-3.5 text-[#84675B]" />
            <FInput data-testid="intake-search" placeholder="Search tasks…" className="pl-9 h-11" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} />
          </div>
          <div className="ml-auto flex flex-wrap gap-2">
            <Btn variant="outline" data-testid="apply-template-btn" onClick={() => setTplOpen(true)} className="rounded-lg"><ShieldCheck size={16} /> Apply Compliance Template</Btn>
            <Btn data-testid="add-intake-task-btn" onClick={openAdd} className="rounded-lg"><Plus size={16} /> Add New Task</Btn>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-3 px-5 pb-5 pt-2 border-b border-[#EFEBE0]">
          <div className="flex gap-1 rounded-full bg-[#F5F2EB] p-1">
            {[["open", "Open"], ["completed", "Completed"], ["all", "All"]].map(([v, l]) => (
              <button key={v} type="button" data-testid={`intake-status-${v}`} onClick={() => { setStatus(v); setPage(1); }}
                className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${status === v ? "bg-[#4E342E] text-white" : "text-[#6D4C41]"}`}>{l}</button>
            ))}
          </div>
          <FSelect data-testid="intake-priority-filter" placeholder="All priorities" className="w-40 h-11" value={priority} onChange={(e) => { setPriority(e.target.value); setPage(1); }}>
            {PRIORITIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </FSelect>
          <div className="flex gap-1 rounded-full bg-[#F5F2EB] p-1">
            {[["today", "Today"], ["tomorrow", "Tomorrow"], ["all", "All Dates"]].map(([v, l]) => (
              <button key={v} type="button" data-testid={`intake-scope-${v}`} onClick={() => setScope(v)}
                className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${scope === v ? "bg-[#8D6E63] text-white" : "text-[#6D4C41]"}`}>{l}</button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-[#6D4C41] whitespace-nowrap">By Date</span>
            <DateField testId="intake-due-start" placeholder="From" className="w-40" value={dueStart} disabledAfter={dueEnd || undefined}
              onChange={(v) => { if (dueEnd && v > dueEnd) { toast.error("Start date cannot be after the end date"); return; } setDueStart(v); setPage(1); }} />
            <span className="text-[#84675B]">to</span>
            <DateField testId="intake-due-end" placeholder="To" className="w-40" value={dueEnd} disabledBefore={dueStart || undefined}
              onChange={(v) => { if (dueStart && v < dueStart) { toast.error("End date cannot be before the start date"); return; } setDueEnd(v); setPage(1); }} />
            {(dueStart || dueEnd) && (
              <button type="button" data-testid="clear-date-filter-btn" onClick={clearDates} className="text-xs font-semibold text-[#7B6157] hover:underline whitespace-nowrap">Clear dates</button>
            )}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead>
              <tr>
                <SortHead field="title">Task</SortHead>
                <th className="bg-[#EDE7DC] text-[#4E342E] font-semibold py-3 px-4">Assignee</th>
                <SortHead field="due_date">Due</SortHead>
                <th className="bg-[#EDE7DC] text-[#4E342E] font-semibold py-3 px-4">Reminder</th>
                <SortHead field="priority">Priority</SortHead>
                <th className="bg-[#EDE7DC] text-[#4E342E] font-semibold py-3 px-4">Status</th>
                <th className="bg-[#EDE7DC] text-[#4E342E] font-semibold py-3 px-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((t) => {
                const done = t.status === "completed" || completingIds.includes(t.id);
                return (
                <tr key={t.id} data-testid={`intake-row-${t.id}`}
                  className={`hover:bg-[#FDFCF8] transition-opacity duration-500 ${t.status === "completed" ? "opacity-60" : ""} ${completingIds.includes(t.id) ? "opacity-30" : ""}`}>
                  <td className={`border-b border-[#EFEBE0] py-3.5 px-4 font-medium max-w-[360px] transition-colors ${done ? "line-through text-[#84675B]" : "text-[#3E2723]"}`}>
                    <Link to={`/engagement/intake-tasks/${t.id}`} data-testid={`intake-title-link-${t.id}`} className="hover:underline">{t.title}</Link>
                    {t.client_id && (
                      <Link to={`/clients/${t.client_id}/console`} data-testid={`intake-client-chip-${t.id}`}
                        className="mt-1 flex w-fit items-center gap-1 rounded-full bg-[#F5F2EB] px-2 py-0.5 text-[10px] font-bold text-[#6D4C41] no-underline hover:bg-[#EDE7DC]">
                        <User size={9} /> {t.client_name || "Client chart"}
                      </Link>
                    )}
                  </td>
                  <td className={`border-b border-[#EFEBE0] py-3.5 px-4 ${done ? "line-through text-[#84675B]" : ""}`}>{t.assignee_name || <span className="text-[#84675B]">Unassigned</span>}</td>
                  <td className={`border-b border-[#EFEBE0] py-3.5 px-4 ${done ? "line-through text-[#84675B]" : ""}`}><DueCell task={t} /></td>
                  <td className="border-b border-[#EFEBE0] py-3.5 px-4">
                    {t.reminder_date ? (
                      <span className="inline-flex items-center gap-1.5 text-[#6D4C41]"><BellRing size={14} className="text-[#7B6157]" /> {dayjs(t.reminder_date).format("MMM D")}</span>
                    ) : <span className="text-[#84675B]">-</span>}
                  </td>
                  <td className="border-b border-[#EFEBE0] py-3.5 px-4"><Badge value={t.priority} /></td>
                  <td className="border-b border-[#EFEBE0] py-3.5 px-4"><Badge value={done ? "completed" : t.status} /></td>
                  <td className="border-b border-[#EFEBE0] py-3.5 px-4">
                    <div className="flex gap-2">
                      {t.status === "open" && (
                        <button data-testid={`complete-intake-${t.id}`} aria-label="Mark complete" title="Mark complete" onClick={() => complete(t)} disabled={completingIds.includes(t.id)}
                          className="h-9 w-9 rounded-lg bg-[#E8F5E9] text-[#2E7D32] flex items-center justify-center hover:bg-[#D5EDD7] transition-colors disabled:opacity-50"><CheckCircle2 size={15} /></button>
                      )}
                      <button data-testid={`edit-intake-${t.id}`} aria-label="Edit" onClick={() => openEdit(t)}
                        className="h-9 w-9 rounded-lg bg-[#F5F2EB] text-[#6D4C41] flex items-center justify-center hover:bg-[#EDE7DC] transition-colors"><Pencil size={14} /></button>
                      <button data-testid={`delete-intake-${t.id}`} aria-label="Delete" onClick={() => setConfirmDelete(t)}
                        className="h-9 w-9 rounded-lg bg-[#F5F2EB] text-[#6D4C41] flex items-center justify-center hover:bg-[#FFEBEE] hover:text-[#C62828] transition-colors"><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
          {data.items.length === 0 && <Empty text={emptyText} />}
        </div>

        {data.pages > 1 && (
          <div className="flex items-center gap-2 p-5" data-testid="intake-pagination">
            <button data-testid="intake-prev-page" disabled={page <= 1} onClick={() => setPage(page - 1)} className="text-xs font-bold uppercase tracking-wide text-[#6D4C41] disabled:opacity-40 px-2">Previous</button>
            {Array.from({ length: data.pages }, (_, i) => i + 1).map((p) => (
              <button key={p} data-testid={`intake-page-${p}`} onClick={() => setPage(p)}
                className={`h-8 w-8 rounded-md text-sm font-semibold transition-colors ${p === page ? "bg-[#4E342E] text-white" : "text-[#6D4C41] hover:bg-[#F5F2EB]"}`}>{p}</button>
            ))}
            <button data-testid="intake-next-page" disabled={page >= data.pages} onClick={() => setPage(page + 1)} className="text-xs font-bold uppercase tracking-wide text-[#6D4C41] disabled:opacity-40 px-2">Next</button>
          </div>
        )}
      </Card>

      <Modal open={modal} onClose={() => setModal(false)} title={editId ? "Edit Task" : "Add New Task"} wide>
        <form onSubmit={save} className="space-y-5">
          <div><FLabel>Task</FLabel><FInput data-testid="intake-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required /></div>
          <div>
            <FLabel>Due Date</FLabel>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-center">
              <DateField testId="intake-due-date" value={form.due_date} onChange={(v) => setForm({ ...form, due_date: v })} />
              <div className="flex flex-wrap gap-1.5">
                {QUICK_DUE.map(([k, l, days]) => (
                  <button key={k} type="button" data-testid={`quick-due-${k}`} onClick={() => setForm({ ...form, due_date: dayjs().add(days, "day").format("YYYY-MM-DD") })}
                    className="rounded-full border border-[#E5DFD3] px-3 py-1.5 text-xs font-semibold text-[#6D4C41] hover:bg-[#F5F2EB] hover:border-[#8D6E63] transition-colors">{l}</button>
                ))}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><FLabel>Priority</FLabel>
              <FSelect data-testid="intake-priority-input" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
                {PRIORITIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </FSelect>
            </div>
            <div><FLabel>Assignee</FLabel>
              <FSelect data-testid="intake-assignee-input" value={form.assignee_id} onChange={(e) => setForm({ ...form, assignee_id: e.target.value })}>
                {staff.map((u) => <option key={u.id} value={u.id}>{u.first_name} {u.last_name} ({String(u.role).replace("_", " ")})</option>)}
              </FSelect>
            </div>
          </div>
          <div>
            <FLabel>Reminder</FLabel>
            <div className="flex items-center gap-3">
              <div className="flex-1"><DateField testId="intake-reminder-date" placeholder="Set a reminder date" value={form.reminder_date} onChange={(v) => setForm({ ...form, reminder_date: v })} /></div>
              {form.reminder_date && (
                <button type="button" data-testid="clear-reminder-btn" onClick={() => setForm({ ...form, reminder_date: "" })} className="text-xs font-semibold text-[#7B6157] hover:underline">Clear</button>
              )}
            </div>
            <p className="mt-1.5 text-xs text-[#84675B] flex items-center gap-1"><BellRing size={12} /> A reminder chip will show on the task from this date.</p>
          </div>
          <div><FLabel>Notes (optional)</FLabel><FTextarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          <div className="flex justify-end gap-2">
            <Btn type="button" variant="ghost" onClick={() => setModal(false)}>Cancel</Btn>
            <Btn data-testid="intake-save-btn" type="submit">{editId ? "Save Changes" : "Add Task"}</Btn>
          </div>
        </form>
      </Modal>

      <Modal open={!!confirmDelete} onClose={() => setConfirmDelete(null)} title="Delete Task">
        <p className="text-sm text-[#6D4C41] mb-5">Delete "<span className="font-semibold text-[#3E2723]">{confirmDelete?.title}</span>"? This cannot be undone.</p>
        <div className="flex justify-end gap-2">
          <Btn variant="ghost" onClick={() => setConfirmDelete(null)}>Cancel</Btn>
          <Btn data-testid="confirm-delete-intake-btn" variant="danger" onClick={doDelete}>Delete</Btn>
        </div>
      </Modal>
    </div>
  );
}
